"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { LinkTrackers } from "~/components/reusables/link-trackers";

interface NavItem {
  id: number;
  url: string;
  cta: string;
  variant: string | null;
  external: boolean | null;
}

interface SecondaryNavProps {
  secondaryNavigation?: NavItem[];
}

export default function SecondaryNav({ secondaryNavigation }: SecondaryNavProps) {
  const pathname = usePathname();
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollTop || document.body.scrollTop;
      const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const progress = windowHeight > 0 ? (totalScroll / windowHeight) * 100 : 0;
      setScrollProgress(progress);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!secondaryNavigation || secondaryNavigation.length === 0) return null;

  // The first item is treated as the "Main/Home" link for this section
  const mainLink = secondaryNavigation[0];
  // The rest are the list items
  const navItems = secondaryNavigation.slice(1);

  // Check if current page is part of this nav ecosystem to show the top gradient
  const isAnyRouteActive = secondaryNavigation.some((item) => pathname === item.url);

  return (
    <>
      <nav
        className="relative z-50 w-full border-b border-white/10 bg-[#050505]"
        aria-label="Secondary Navigation"
      >
        {isAnyRouteActive && (
          <div className="absolute top-0 h-[1px] w-full bg-gradient-blue-purple"></div>
        )}
        
        <div className="container flex items-center px-3 md:px-6">
          {/* Main Section Link */}
          <div className="flex-shrink-0">
            <LinkTrackers
              target={mainLink && mainLink.external ? "_blank" : "_self"}
              href={mainLink?.url || "/"}
              className="group relative flex items-center px-2 pb-2 pt-4 md:px-3 md:pt-5 transition duration-300 ease-in-out"
              segmentMsg={"Jump link clicked"}
              segmentOpt={{
                location: "secondary_nav",
                link_label: mainLink?.cta,
                resource_interaction: `go to ${mainLink?.cta}`,
              }}
            >
              <span className={`text-sm md:text-[17px] transition-colors ${
                pathname === mainLink?.url 
                  ? "font-normal text-white/40 hover:text-white/70" 
                  : "font-normal text-white/40 hover:text-white/70"
              }`}>
                {mainLink?.cta}
              </span>
            </LinkTrackers>
          </div>

          {/* Separator */}
          {navItems.length > 0 && (
            <div
              className="mx-0 h-6 w-[1px] bg-white/20 md:mx-6"
              role="separator"
              aria-orientation="vertical"
            />
          )}

          {/* Dynamic List Items */}
          <ul className="flex h-full items-center text-center md:gap-x-2">
            {navItems.map((item) => {
              const isActive = pathname === item.url;
              return (
                <li key={item.id} className="h-full">
                  <LinkTrackers
                    target={item.external ? "_blank" : "_self"}
                    href={item.url || "/"}
                    className={`group relative flex items-center border-b-4 px-3 pb-2 pt-4 text-sm md:text-[17px] transition duration-300 ease-in-out md:px-4 md:pt-5 ${
                      isActive
                        ? "border-custom-yellow font-bold text-white"
                        : "border-transparent font-normal text-white/90 hover:border-custom-yellow/50 hover:text-white"
                    }`}
                    segmentMsg={"Jump link clicked"}
                    segmentOpt={{
                      location: "secondary_nav",
                      link_label: item.cta,
                      resource_interaction: `go to ${item.cta} page`,
                    }}
                  >
                    {item.cta}
                  </LinkTrackers>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>

      {/* Sticky Read Indicator */}
      <div className="sticky top-0 z-50 w-full h-[3px]">
        <div className="absolute top-0 left-0 h-[3px] w-full bg-[#1A1A1A] lg:bg-transparent"></div>
        <div
          className="absolute top-0 left-0 h-[3px] bg-gradient-to-r from-[#9333EA] via-[#3B82F6] via-[#06B6D4] via-[#FACC15] via-[#F97316] to-[#EC4899] z-10 transition-all duration-150 ease-out"
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>
    </>
  );
}